import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Newspaper, Columns2, Map as MapIcon, BarChart3, Gauge, Building2, Siren, Rss, Bookmark } from "lucide-react";
import { fetchNewsMeta, fetchAlerts } from "../lib/newsApi";
import { timeAgo } from "../lib/tone";
import { FeedView } from "../components/news/FeedView";
import { CompareView } from "../components/news/CompareView";
import { MapView } from "../components/news/MapView";
import { AnalyticsView } from "../components/news/AnalyticsView";
import { SentimentView } from "../components/news/SentimentView";
import { SourcesView } from "../components/news/SourcesView";
import { AlertsView } from "../components/news/AlertsView";
import { RssView } from "../components/news/RssView";
import { SavedView } from "../components/news/SavedView";
import { StoryDialog } from "../components/news/StoryDetail";

const TABS = [["feed", "Feed", Newspaper], ["compare", "Compare", Columns2], ["map", "Map", MapIcon], ["analytics", "Analytics", BarChart3],
  ["sentiment", "News tone", Gauge], ["sources", "Ownership", Building2], ["alerts", "Alerts", Siren], ["feeds", "RSS feeds", Rss], ["saved", "Saved", Bookmark]];
const DEFAULT_FILTERS = { sort: "relevance", dedupe: true, balanced: true, hours: 168 };

export default function News() {
  const [params, setParams] = useSearchParams();
  const tab = TABS.some(([t]) => t === params.get("tab")) ? params.get("tab") : "feed";
  const [meta, setMeta] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [story, setStory] = useState(null);
  const [breaking, setBreaking] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => { fetchNewsMeta().then(setMeta).catch(() => {}); fetchAlerts({ type: "breaking", hours: 24 }).then((r) => setBreaking(r.items.length)).catch(() => {}); }, [refreshKey]);
  const go = (t) => setParams(t === "feed" ? {} : { tab: t });
  const applyFilter = (f) => { setFilters({ ...DEFAULT_FILTERS, ...f }); go("feed"); window.scrollTo({ top: 0, behavior: "smooth" }); };
  useEffect(() => { const s = params.get("story"); if (s) setStory(s); }, [params]);
  const closeStory = useCallback(() => {
    setStory(null);
    setParams((p) => { if (!p.has("story")) return p; const n = new URLSearchParams(p); n.delete("story"); return n; }, { replace: true });
  }, [setParams]);
  const open = setStory;

  return (
    <div className="mx-auto max-w-7xl px-5 py-10 sm:px-8 sm:py-14" data-testid="news-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Link to="/pulse" className="text-sm font-semibold text-sky-800 hover:underline" data-testid="news-back-to-pulse">← Back to Community Pulse</Link>
          <p className="mt-4 text-xs font-semibold uppercase tracking-widest text-sky-800" data-testid="news-page-eyebrow">Sources & media tools</p>
          <h1 className="mt-3 font-serif text-3xl tracking-tight text-slate-900 sm:text-4xl">Where our community stories come from</h1>
          <p className="mt-3 max-w-2xl text-sm text-slate-700 sm:text-base">The outlets behind Community Pulse — national, independent and community newspapers — with ownership, coverage comparison and tone tools.</p>
        </div>
        <div className="text-xs text-slate-500 sm:text-right">
          {breaking > 0 && <button onClick={() => go("alerts")} className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-rose-600 px-3 py-1 font-medium text-white" data-testid="breaking-pill"><Siren size={13} />{breaking} breaking</button>}
          <p data-testid="news-last-refresh">{meta?.last_refresh ? `Updated ${timeAgo(meta.last_refresh)} · every ${meta.refresh_minutes} min` : ""}</p>
        </div>
      </div>
      <nav className="mt-8 flex gap-1 overflow-x-auto border-b border-slate-200" role="tablist" aria-label="Dashboard views">
        {TABS.map(([t, l, Icon]) => (
          <button key={t} role="tab" aria-selected={tab === t} onClick={() => go(t)} data-testid={`news-tab-${t}`}
            className={`inline-flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-3 text-sm transition-colors ${tab === t ? "border-sky-900 font-medium text-sky-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            <Icon size={15} />{l}
          </button>
        ))}
      </nav>
      <div className="mt-8">
        {tab === "feed" && <FeedView meta={meta} filters={filters} setFilters={setFilters} onOpenStory={open} refreshKey={refreshKey} />}
        {tab === "compare" && <CompareView />}
        {tab === "map" && <MapView onFilter={applyFilter} onOpenStory={open} />}
        {tab === "analytics" && <AnalyticsView onOpenStory={open} onFilter={applyFilter} />}
        {tab === "sentiment" && <SentimentView />}
        {tab === "sources" && <SourcesView onFilter={applyFilter} />}
        {tab === "alerts" && <AlertsView onOpenStory={open} refreshKey={refreshKey} />}
        {tab === "feeds" && <RssView onRefreshed={() => setRefreshKey((k) => k + 1)} />}
        {tab === "saved" && <SavedView onApply={applyFilter} onOpenStory={open} />}
      </div>
      <StoryDialog id={story} onClose={closeStory} />
    </div>
  );
}
